import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/index.js';
import { useAuth } from '../../context/index.js';
import { useApp } from '../../context/index.js';
import { Mountain, Menu, X, ShoppingCart, User, Search, Heart, ArrowLeft, ChevronUp, ArrowRight, Compass, BookOpen, UtensilsCrossed, Tag, Facebook, Instagram, Youtube, Twitter, Linkedin, MessageCircle, Music2 } from 'lucide-react';
import ApiService from '../../services/index.js';
import { ADMIN_PANEL_ROLES } from '../../utils/permissions';
import { buildIndex, searchIndex } from '../../utils/siteSearch';
import PropTypes from 'prop-types';

const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const { getCartItemCount } = useCart();
  const { user, logout } = useAuth();
  const { restaurantInfo } = useApp();
  const isAdmin = !!user && ADMIN_PANEL_ROLES.includes(user.role);
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const profileRef = useRef(null);
  const searchInputRef = useRef(null);

  const [searchIndexState, setSearchIndexState] = useState(null);
  const [searchIndexLoading, setSearchIndexLoading] = useState(false);

  const tableNumber = params.tableNumber;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [showSearch]);

  useEffect(() => {
    if (!showSearch || searchIndexState || searchIndexLoading) return;
    let cancelled = false;
    setSearchIndexLoading(true);
    (async () => {
      try {
        const [menuRes, catRes] = await Promise.allSettled([
          ApiService.getMenuItems?.('', false, { cache: true }),
          ApiService.getCategories?.()
        ]);
        if (cancelled) return;
        const menuItems = (() => {
          if (menuRes.status !== 'fulfilled') return [];
          const data = menuRes.value?.data ?? menuRes.value ?? [];
          return Array.isArray(data) ? data : (data.items || []);
        })();
        const categories = (() => {
          if (catRes.status !== 'fulfilled') return [];
          const data = catRes.value?.data ?? catRes.value ?? [];
          return Array.isArray(data) ? data : (data.categories || []);
        })();
        setSearchIndexState(buildIndex({ menuItems, categories }));
      } catch (_) {
        setSearchIndexState(buildIndex({}));
      } finally {
        if (!cancelled) setSearchIndexLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showSearch, searchIndexState, searchIndexLoading]);

  const livePreviewResults = (() => {
    if (!showSearch || !searchQuery.trim() || !searchIndexState) return [];
    return searchIndex(searchIndexState, searchQuery, { limit: 6 });
  })();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowSearch(false);
        setIsProfileOpen(false);
        setIsMenuOpen(false);
      }
    };

    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsProfileOpen(false);
    setShowSearch(false);
  }, [location.pathname]);

  const STAFF_NAV_BY_ROLE = {
    admin: [
      { path: '/admin/dashboard',    label: 'Dashboard' },
      { path: '/admin/orders',       label: 'Orders' },
      { path: '/admin/reservations', label: 'Reservations' },
      { path: '/admin/analytics',    label: 'Analytics' },
      { path: '/admin/settings',     label: 'Settings' },
    ],
    manager: [
      { path: '/admin/dashboard',    label: 'Dashboard' },
      { path: '/admin/orders',       label: 'Orders' },
      { path: '/admin/reservations', label: 'Reservations' },
      { path: '/admin/analytics',    label: 'Analytics' },
      { path: '/admin/staff',        label: 'Staff' },
    ],
    chef: [
      { path: '/admin/kitchen',      label: 'Kitchen' },
      { path: '/admin/menu',         label: 'Menu' },
    ],
    waiter: [
      { path: '/admin/floor',        label: 'Floor' },
      { path: '/admin/reservations', label: 'Reservations' },
      { path: '/admin/tables',       label: 'Tables' },
    ],
    cashier: [
      { path: '/admin/orders',       label: 'Orders' },
      { path: '/admin/invoices',     label: 'Invoices' },
      { path: '/admin/reservations', label: 'Reservations' },
    ],
    staff: [
      { path: '/admin/orders',       label: 'Orders' },
      { path: '/admin/reservations', label: 'Reservations' },
      { path: '/admin/invoices',     label: 'Invoices' },
    ],
  };

  const navItems = user
    ? isAdmin
      ? (STAFF_NAV_BY_ROLE[user.role] || STAFF_NAV_BY_ROLE.staff)
      : [
          { path: '/', label: 'Home' },
          { path: '/menu', label: 'Menu' },
          { path: '/reservations', label: 'Reservations' },
          { path: '/gallery', label: 'Gallery' },
          { path: '/about', label: 'About' },
          { path: '/contact', label: 'Contact' },
          { path: '/my-bookings', label: 'My Bookings' },
        ]
    : [
        { path: '/', label: 'Home' },
        { path: '/menu', label: 'Menu' },
        { path: '/reservations', label: 'Reservations' },
        { path: '/gallery', label: 'Gallery' },
        { path: '/about', label: 'About' },
        { path: '/contact', label: 'Contact' },
      ];

  const isActive = (path) => location.pathname === path;
  const isCartPage = location.pathname.includes('/cart');
  const isFavoritesPage = location.pathname === '/favorites';
  const showBackButton = isCartPage || location.pathname.includes('/order/');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSearch(false);
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const toggleSearch = () => {
    setShowSearch(!showSearch);
  };

  const handleLogout = () => {
    logout();
    setIsProfileOpen(false);
    setIsMenuOpen(false);
    navigate('/');
  };

  const handleQuickSearch = (term) => {
    setShowSearch(false);
    setSearchQuery('');
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  const handlePreviewResultClick = (entry) => {
    setShowSearch(false);
    setSearchQuery('');
    navigate(entry.url);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen flex flex-col lyt2">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Inter:wght@300;400;500;600&display=swap');
        .lyt2 {
          background: #0D0D0D;
          color: #E2D0B0;
          font-family: 'Inter', system-ui, sans-serif;
        }
        .lyt2-serif {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-weight: 300;
          letter-spacing: 0.04em;
        }
        .lyt2-gold { color: #C4A25A; }
        .lyt2-cream { color: #E2D0B0; }
        .lyt2-muted { color: #6B6B6B; }
        .lyt2-header-bg { background: rgba(13,13,13,0.96); border-bottom: 1px solid rgba(196,162,90,0.18); backdrop-filter: blur(12px); }
        .lyt2-nav-active { color: #C4A25A; background: rgba(196,162,90,0.08); }
        .lyt2-nav-inactive { color: #6B6B6B; }
        .lyt2-nav-inactive:hover { color: #C4A25A; background: rgba(196,162,90,0.05); }
        .lyt2-icon-btn { color: #6B6B6B; border-radius: 0; transition: color 0.2s; }
        .lyt2-icon-btn:hover { color: #C4A25A; background: rgba(196,162,90,0.06); }
        .lyt2-icon-btn-active { color: #C4A25A; background: rgba(196,162,90,0.08); }
        .lyt2-btn-gold {
          background: rgba(196,162,90,0.12);
          color: #C4A25A;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          font-size: 0.7rem;
          padding: 0.5rem 1rem;
          border: 1px solid rgba(196,162,90,0.4);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          transition: background 0.2s, color 0.2s, box-shadow 0.2s;
        }
        .lyt2-btn-gold:hover { background: rgba(196,162,90,0.22); color: #D4B870; box-shadow: 0 0 14px rgba(196,162,90,0.25); }
        .lyt2-dropdown {
          background: #0D0D0D;
          border: 1px solid rgba(196,162,90,0.22);
          border-radius: 0;
          box-shadow: 0 8px 32px rgba(0,0,0,0.7);
        }
        .lyt2-dropdown-item { color: #6B6B6B; transition: color 0.2s, background 0.2s; }
        .lyt2-dropdown-item:hover { color: #C4A25A; background: rgba(196,162,90,0.05); }
        .lyt2-dropdown-logout:hover { color: #f87171; background: rgba(248,113,113,0.05); }
        .lyt2-mobile-panel { background: rgba(13,13,13,0.99); border-top: 1px solid rgba(196,162,90,0.18); }
        .lyt2-mobile-active { color: #C4A25A; background: rgba(196,162,90,0.08); border-left: 2px solid #C4A25A; }
        .lyt2-mobile-inactive { color: #6B6B6B; }
        .lyt2-mobile-inactive:hover { color: #C4A25A; background: rgba(196,162,90,0.05); }
        .lyt2-footer { background: #0D0D0D; border-top: 1px solid rgba(196,162,90,0.18); }
        .lyt2-footer-link { color: #6B6B6B; transition: color 0.25s; }
        .lyt2-footer-link:hover { color: #C4A25A; }
        .lyt2-footer-arrow { background: #C4A25A; }
        .lyt2-social-btn {
          width: 2.25rem; height: 2.25rem;
          border: 1px solid rgba(196,162,90,0.22);
          color: #6B6B6B;
          display: flex; align-items: center; justify-content: center;
          transition: border-color 0.2s, color 0.2s;
        }
        .lyt2-social-btn:hover { border-color: #C4A25A; color: #C4A25A; }
        .lyt2-scrolltop {
          padding: 0.45rem;
          background: rgba(196,162,90,0.14);
          color: #C4A25A;
          border-radius: 0;
          border: 1px solid rgba(196,162,90,0.38);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          box-shadow: 0 2px 12px rgba(0,0,0,0.3);
          transition: background 0.2s, transform 0.2s, opacity 0.3s;
        }
        .lyt2-scrolltop:hover { background: rgba(196,162,90,0.26); transform: scale(1.06); }
        .lyt2-search-modal {
          background: #0D0D0D;
          border: 1px solid rgba(196,162,90,0.22);
          border-radius: 0;
          box-shadow: 0 16px 48px rgba(0,0,0,0.8);
        }
        .lyt2-search-result:hover { background: rgba(196,162,90,0.04); }
        @keyframes scrollArrowFlash {
          0%, 100% { opacity: 1; filter: drop-shadow(0 0 8px rgba(196,162,90,0.9)) drop-shadow(0 0 16px rgba(196,162,90,0.5)); }
          50%       { opacity: 0.15; filter: drop-shadow(0 0 2px rgba(196,162,90,0.2)); }
        }
        .scroll-arrow-glow { animation: scrollArrowFlash 1s ease-in-out infinite; }
        .lyt2-search-icon-wrap {
          background: rgba(196,162,90,0.08);
          border: 1px solid rgba(196,162,90,0.22);
          border-radius: 0;
        }
        .lyt2-pill {
          background: rgba(196,162,90,0.08);
          color: #C4A25A;
          border: 1px solid rgba(196,162,90,0.22);
          border-radius: 0;
          font-size: 0.75rem;
          padding: 0.35rem 0.75rem;
          transition: background 0.2s;
        }
        .lyt2-pill:hover { background: rgba(196,162,90,0.15); }
        .lyt2-divider { border-color: rgba(196,162,90,0.15); }
        @keyframes lyt2-fade-in {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .lyt2-animate-in { animation: lyt2-fade-in 0.2s ease-out forwards; }
        .lyt2-cart-badge {
          background: #C4A25A;
          color: #0D0D0D;
          font-size: 0.6rem;
          font-weight: 700;
          width: 1.2rem; height: 1.2rem;
          border-radius: 0;
          display: flex; align-items: center; justify-content: center;
          position: absolute; top: -0.25rem; right: -0.25rem;
        }
      `}</style>

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'lyt2-header-bg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">

            {/* Left: Back button + Logo */}
            <div className="flex items-center">
              {showBackButton && (
                <button
                  onClick={() => navigate(-1)}
                  className="glass-btn flex items-center mr-3 md:mr-6 px-3 py-2"
                  aria-label="Go back"
                >
                  <ArrowLeft className="w-5 h-5 mr-1 md:mr-2" />
                  <span className="hidden sm:inline text-sm tracking-widest uppercase" style={{fontSize:'0.65rem'}}>Back</span>
                </button>
              )}

              <Link to="/" className="flex items-center group">
                <Mountain className="w-7 h-7 md:w-8 md:h-8 lyt2-gold mr-2 md:mr-3 transform group-hover:scale-110 transition-transform duration-300" />
                <div className="flex flex-col">
                  <span className="lyt2-serif lyt2-cream" style={{fontSize:'1.15rem',letterSpacing:'0.08em'}}>Swat Capital</span>
                  <div className="flex items-center gap-2" style={{marginTop:'-2px'}}>
                    <span className="lyt2-muted" style={{fontSize:'0.65rem',letterSpacing:'0.18em',textTransform:'uppercase'}}>Resort</span>
                    {tableNumber && (
                      <span style={{fontSize:'0.6rem',background:'rgba(196,162,90,0.12)',color:'#C4A25A',padding:'1px 7px',border:'1px solid rgba(196,162,90,0.28)',letterSpacing:'0.1em'}}>
                        Table {tableNumber}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            </div>

            {/* Center: Navigation (desktop) */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative px-4 py-2 transition-all duration-200 ${
                    isActive(item.path) ? 'lyt2-nav-active' : 'lyt2-nav-inactive'
                  }`}
                  style={{fontSize:'0.72rem',letterSpacing:'0.12em',textTransform:'uppercase'}}
                >
                  {item.label}
                  {isActive(item.path) && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-4 h-px" style={{background:'#C4A25A'}}></div>
                  )}
                </Link>
              ))}
            </nav>

            {/* Right: Actions */}
            <div className="flex items-center space-x-1 md:space-x-2">

              {/* Search — hidden for admin */}
              {!isAdmin && (
                <button
                  onClick={toggleSearch}
                  className={`p-2 lyt2-icon-btn ${showSearch ? 'lyt2-icon-btn-active' : ''}`}
                  aria-label="Search"
                >
                  <Search className="w-5 h-5" />
                </button>
              )}

              {/* Favorites — hidden for admin */}
              {user && !isAdmin && (
                <Link
                  to="/favorites"
                  className={`p-2 lyt2-icon-btn ${isFavoritesPage ? 'lyt2-icon-btn-active' : ''}`}
                  style={isFavoritesPage ? {color:'#f87171'} : {}}
                  aria-label="Favorites"
                >
                  <Heart className={`w-5 h-5 ${isFavoritesPage ? 'fill-current' : ''}`} style={isFavoritesPage ? {color:'#f87171'} : {}} />
                </Link>
              )}

              {/* Cart — hidden for admin */}
              {!isAdmin && (
                <Link
                  to="/cart"
                  className={`relative p-2 lyt2-icon-btn ${isCartPage ? 'lyt2-icon-btn-active' : ''}`}
                  aria-label={`Cart with ${getCartItemCount()} items`}
                >
                  <ShoppingCart className="w-5 h-5" />
                  {getCartItemCount() > 0 && (
                    <span className="lyt2-cart-badge">{getCartItemCount()}</span>
                  )}
                </Link>
              )}

              {/* Profile dropdown */}
              {user && (
                <div className="relative hidden md:block" ref={profileRef}>
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="lyt2-btn-gold flex items-center"
                  >
                    <User className="w-3.5 h-3.5 md:mr-2" />
                    <span className="hidden sm:inline">{user.name?.split(' ')[0] || 'Profile'}</span>
                  </button>

                  {isProfileOpen && (
                    <div className="absolute top-full right-0 mt-2 w-48 lyt2-dropdown py-1 z-50 lyt2-animate-in">
                      <Link
                        to={isAdmin ? '/admin/profile' : '/profile'}
                        className="block px-4 py-2.5 text-sm lyt2-dropdown-item"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        Profile
                      </Link>
                      {!isAdmin && (
                        <Link
                          to="/favorites"
                          className="block px-4 py-2.5 text-sm lyt2-dropdown-item"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          My Favorites
                        </Link>
                      )}
                      {(user.role === 'admin' || user.role === 'manager') && (
                        <Link
                          to="/admin/settings"
                          className="block px-4 py-2.5 text-sm lyt2-dropdown-item"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          Settings
                        </Link>
                      )}
                      <hr className="my-1 lyt2-divider" />
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2.5 text-sm lyt2-dropdown-item lyt2-dropdown-logout"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Login button */}
              {!user && (
                <Link
                  to="/login"
                  className="lyt2-btn-gold flex items-center"
                >
                  <User className="w-3.5 h-3.5 md:mr-2" />
                  <span className="hidden sm:inline">Login</span>
                </Link>
              )}

              {/* Mobile menu toggle */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-2 lyt2-icon-btn"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

      </header>

      {/* Mobile Navigation — moved outside <header> to fix fixed-inside-backdrop-filter bug */}
      <div
        className={`lg:hidden fixed inset-0 transition-all duration-300 ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ top: '64px', zIndex: 9999 }}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0"
          style={{background:'rgba(0,0,0,0.75)',backdropFilter:'blur(4px)'}}
          onClick={() => setIsMenuOpen(false)}
        />

        {/* Menu panel */}
        <div className={`relative lyt2-mobile-panel h-full overflow-y-auto transition-transform duration-300 ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="px-4 py-6 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`block py-3 px-4 transition-all duration-200 ${
                  isActive(item.path) ? 'lyt2-mobile-active' : 'lyt2-mobile-inactive'
                }`}
                style={{fontSize:'0.72rem',letterSpacing:'0.14em',textTransform:'uppercase'}}
              >
                {item.label}
              </Link>
            ))}

            {user && (
              <>
                <hr className="lyt2-divider my-4" />
                {!isAdmin && (
                  <Link
                    to="/favorites"
                    className={`block py-3 px-4 transition-all duration-200 ${
                      isFavoritesPage ? 'lyt2-mobile-active' : 'lyt2-mobile-inactive'
                    }`}
                    style={{fontSize:'0.72rem',letterSpacing:'0.14em',textTransform:'uppercase'}}
                  >
                    <Heart className="w-4 h-4 inline mr-2" />
                    My Favorites
                  </Link>
                )}
                <Link
                  to={isAdmin ? '/admin/profile' : '/profile'}
                  className="block py-3 px-4 lyt2-mobile-inactive transition-all"
                  style={{fontSize:'0.72rem',letterSpacing:'0.14em',textTransform:'uppercase'}}
                >
                  <User className="w-4 h-4 inline mr-2" />
                  Profile
                </Link>
                {(user.role === 'admin' || user.role === 'manager') && (
                  <Link
                    to="/admin/settings"
                    className="block py-3 px-4 lyt2-mobile-inactive transition-all"
                    style={{fontSize:'0.72rem',letterSpacing:'0.14em',textTransform:'uppercase'}}
                  >
                    Settings
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="block w-full text-left py-3 px-4 transition-all"
                  style={{fontSize:'0.72rem',letterSpacing:'0.14em',textTransform:'uppercase',color:'#f87171'}}
                >
                  Logout
                </button>
              </>
            )}

            {!user && (
              <Link
                to="/login"
                className="block py-3 px-4 text-center mt-4 lyt2-btn-gold"
                style={{display:'block',textAlign:'center',marginTop:'1rem'}}
              >
                Login / Sign Up
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-grow pt-16 md:pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className="lyt2-footer relative overflow-hidden">
        <div className="absolute inset-0" style={{background:'linear-gradient(135deg,rgba(196,162,90,0.03) 0%,transparent 60%)'}}></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">

            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center mb-4 md:mb-6">
                <Mountain className="w-8 h-8 md:w-10 md:h-10 lyt2-gold mr-3 md:mr-4" />
                <div>
                  <h3 className="lyt2-serif lyt2-cream" style={{fontSize:'1.4rem',letterSpacing:'0.06em'}}>{restaurantInfo?.name || 'Swat Capital Resort'}</h3>
                  <p className="lyt2-muted" style={{fontSize:'0.65rem',letterSpacing:'0.18em',textTransform:'uppercase',marginTop:'2px'}}>{restaurantInfo?.description || 'Experience luxury redefined'}</p>
                </div>
              </div>
              <p className="lyt2-muted text-sm md:text-base mb-4 md:mb-6 max-w-md leading-relaxed" style={{lineHeight:'1.8'}}>
                Nestled in the heart of Pakistan&apos;s Switzerland, we offer an unparalleled blend of natural beauty,
                luxury accommodation, and exceptional hospitality.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="lyt2-gold mb-3 md:mb-5" style={{fontSize:'0.65rem',letterSpacing:'0.2em',textTransform:'uppercase',fontWeight:500}}>Quick Links</h4>
              <ul className="space-y-2 md:space-y-3">
                {navItems.slice(0, 6).map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className="lyt2-footer-link flex items-center group text-sm"
                      style={{fontSize:'0.78rem',letterSpacing:'0.06em'}}
                    >
                      <span className="w-0 group-hover:w-2 h-px lyt2-footer-arrow transition-all duration-300 mr-0 group-hover:mr-2 flex-shrink-0" style={{display:'inline-block'}}></span>
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="lyt2-gold mb-3 md:mb-5" style={{fontSize:'0.65rem',letterSpacing:'0.2em',textTransform:'uppercase',fontWeight:500}}>Contact Info</h4>
              <div className="space-y-2 md:space-y-3 lyt2-muted text-sm">
                <a href="https://www.google.com/maps/place/Swat+Capital+Resort/@34.7387884,72.3700245,17z/data=!3m1!4b1!4m6!3m5!1s0x38dc23cbf61604e7:0xbecb195e42dbf097!8m2!3d34.7387884!4d72.3700245!16s%2Fg%2F11ft5sbxxj" target="_blank" rel="noopener noreferrer" className="lyt2-footer-link flex items-start">
                  <span className="w-1 h-1 flex-shrink-0 mt-2 mr-3" style={{background:'#C4A25A',display:'inline-block'}}></span>
                  <span style={{fontSize:'0.78rem',lineHeight:'1.6'}}>{restaurantInfo?.address || ''}</span>
                </a>
                <a href={`tel:${(restaurantInfo?.phone || '+923329622132').replace(/\s+/g, '')}`} className="lyt2-footer-link flex items-center">
                  <span className="w-1 h-1 mr-3 flex-shrink-0" style={{background:'#C4A25A',display:'inline-block'}}></span>
                  <span style={{fontSize:'0.78rem'}}>{restaurantInfo?.phone || ''}</span>
                </a>
                <a href={`mailto:${restaurantInfo?.email || ''}`} className="lyt2-footer-link flex items-center">
                  <span className="w-1 h-1 mr-3 flex-shrink-0" style={{background:'#C4A25A',display:'inline-block'}}></span>
                  <span style={{fontSize:'0.78rem'}}>{restaurantInfo?.email || ''}</span>
                </a>
              </div>
            </div>
          </div>

          {/* Social media */}
          {(() => {
            const socials = [
              { key: 'facebook',  label: 'Facebook',    Icon: Facebook,      url: restaurantInfo?.facebook  },
              { key: 'instagram', label: 'Instagram',   Icon: Instagram,     url: restaurantInfo?.instagram },
              { key: 'tiktok',    label: 'TikTok',      Icon: Music2,        url: restaurantInfo?.tiktok    },
              { key: 'youtube',   label: 'YouTube',     Icon: Youtube,       url: restaurantInfo?.youtube   },
              { key: 'twitter',   label: 'X / Twitter', Icon: Twitter,       url: restaurantInfo?.twitter   },
              { key: 'linkedin',  label: 'LinkedIn',    Icon: Linkedin,      url: restaurantInfo?.linkedin  },
              { key: 'whatsapp',  label: 'WhatsApp',    Icon: MessageCircle, url: restaurantInfo?.whatsapp ? `https://wa.me/${String(restaurantInfo.whatsapp).replace(/[^\d]/g,'')}` : '' },
            ].filter((s) => !!s.url && String(s.url).trim().length > 0);
            if (socials.length === 0) return null;
            return (
              <div className="mt-8 md:mt-12 pt-6 md:pt-8 flex flex-wrap items-center justify-center gap-3 lyt2-divider" style={{borderTop:'1px solid rgba(196,162,90,0.15)'}}>
                {socials.map(({ key, label, Icon, url }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    title={label}
                    className="lyt2-social-btn"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            );
          })()}

          <div className="mt-6 md:mt-8 pt-6 md:pt-8 text-center" style={{borderTop:'1px solid rgba(196,162,90,0.12)'}}>
            <p className="lyt2-muted" style={{fontSize:'0.68rem',letterSpacing:'0.1em'}}>
              &copy; {new Date().getFullYear()} {restaurantInfo?.name || 'Swat Capital Resort'}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Scroll to top button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-20 left-4 md:left-auto md:bottom-24 md:right-6 z-40 transition-all duration-300 p-1 ${
          isScrolled ? 'opacity-100 scale-100' : 'opacity-0 scale-0 pointer-events-none'
        }`}
        style={{background:'transparent',border:'none',color:'#C4A25A',boxShadow:'none'}}
        aria-label="Scroll to top"
      >
        <ChevronUp className="w-5 h-5 scroll-arrow-glow" />
      </button>

      {/* Search Modal */}
      {showSearch && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center pt-20 md:pt-24 px-4"
          style={{background:'rgba(0,0,0,0.8)',backdropFilter:'blur(6px)'}}
          onClick={(e) => e.target === e.currentTarget && setShowSearch(false)}
        >
          <div className="lyt2-search-modal max-w-2xl w-full overflow-hidden lyt2-animate-in">
            <form onSubmit={handleSearch} className="p-4 md:p-6 pb-4">
              <div className="flex items-center gap-3 md:gap-4">
                <Search className="w-5 h-5 md:w-6 md:h-6 lyt2-gold flex-shrink-0" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search anything — pages, dishes, info…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 outline-none bg-transparent lyt2-cream"
                  style={{fontSize:'1.1rem',color:'#E2D0B0'}}
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowSearch(false)}
                  className="glass-btn p-2 flex-shrink-0"
                  aria-label="Close search"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </form>

            {/* Live preview results */}
            {searchQuery.trim() && (
              <div className="max-h-[60vh] overflow-y-auto" style={{borderTop:'1px solid rgba(196,162,90,0.15)'}}>
                {searchIndexLoading && livePreviewResults.length === 0 ? (
                  <div className="p-6 text-center text-sm lyt2-muted">Loading search index…</div>
                ) : livePreviewResults.length === 0 ? (
                  <div className="p-6 text-center">
                    <p className="lyt2-muted text-sm mb-3">No matches yet — try a different word.</p>
                    <button
                      type="button"
                      onClick={() => handleQuickSearch(searchQuery)}
                      className="inline-flex items-center gap-2 text-sm underline"
                      style={{color:'#C4A25A'}}
                    >
                      Search the full site for &ldquo;{searchQuery}&rdquo;
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <ul className="py-2">
                    {livePreviewResults.map(({ entry }) => {
                      const TypeIcon = entry.type === 'dish' ? UtensilsCrossed
                        : entry.type === 'category' ? Tag
                        : entry.type === 'info' ? BookOpen
                        : Compass;
                      return (
                        <li key={entry.id}>
                          <button
                            type="button"
                            onClick={() => handlePreviewResultClick(entry)}
                            className="w-full flex items-center gap-3 px-4 md:px-6 py-3 lyt2-search-result transition-colors text-left group"
                          >
                            <div className="w-9 h-9 lyt2-search-icon-wrap flex items-center justify-center flex-shrink-0">
                              <TypeIcon className="w-4 h-4 lyt2-gold" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="lyt2-cream font-medium truncate" style={{fontSize:'0.85rem',transition:'color 0.15s'}} onMouseEnter={e=>e.currentTarget.style.color='#D4B870'} onMouseLeave={e=>e.currentTarget.style.color=''}>
                                {entry.title}
                              </div>
                              {entry.subtitle && (
                                <div className="lyt2-muted truncate" style={{fontSize:'0.7rem',marginTop:'1px'}}>{entry.subtitle}</div>
                              )}
                            </div>
                            <ArrowRight className="w-4 h-4 lyt2-muted group-hover:translate-x-0.5 transition-all flex-shrink-0" style={{transition:'color 0.15s,transform 0.15s'}} />
                          </button>
                        </li>
                      );
                    })}
                    <li className="px-4 md:px-6 pt-2 pb-3 mt-1" style={{borderTop:'1px solid rgba(196,162,90,0.12)'}}>
                      <button
                        type="button"
                        onClick={() => handleQuickSearch(searchQuery)}
                        className="w-full text-left text-sm inline-flex items-center gap-2"
                        style={{color:'#C4A25A',fontSize:'0.75rem',letterSpacing:'0.06em'}}
                      >
                        See all results for &ldquo;{searchQuery}&rdquo;
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            )}

            {/* Quick searches (only when input is empty) */}
            {!searchQuery.trim() && (
              <div className="px-4 md:px-6 py-4" style={{borderTop:'1px solid rgba(196,162,90,0.15)'}}>
                <p className="lyt2-muted mb-3" style={{fontSize:'0.65rem',letterSpacing:'0.16em',textTransform:'uppercase'}}>Popular searches</p>
                <div className="flex flex-wrap gap-2">
                  {['Menu', 'Reservations', 'Opening hours', 'Vegetarian', 'Contact', 'Gallery', 'Cancel booking'].map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => handleQuickSearch(suggestion)}
                      className="lyt2-pill"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired
};

export default Layout;